import React, { useRef, useState } from 'react';
import { UnstyledButton } from '@/components/ui/Buttons';
import Text from '@/components/ui/Text';
import I18n from '@/modules/i18n';
import TagManagerTable from './TagManagerTable';
import useModelCorrelations from './hooks/useModelCorrelations';
import { TagManagerVariant } from './types';

interface TagManagerProps {
  /** Server-rendered count for the collapsed trigger, so it reads correctly before any fetch. */
  initialCount?: number;
  /** Section label ("Tags"), rendered inline with the trigger. Omit to render the trigger alone. */
  label?: string;
  modelId: number;
  modelType: string;
  /** The program whose counts a mutation should refresh. Absent on surfaces with no program. */
  programId?: number;
  variant?: TagManagerVariant;
}

// Inline and in-card placements. The Slide panel has its own chrome and renders TagManagerTable directly.
const TagManager = ({
  initialCount = 0, label, modelId, modelType, programId, variant = 'inline',
}: TagManagerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // One manager per content item, so this is unique without useId (React 16).
  const panelId = `tag-manager-${modelType}-${modelId}`;

  const {
    categories, correlations, isError, isLoading,
  } = useModelCorrelations({ enabled: isOpen, modelId, modelType });

  const open = () => {
    setIsOpen(true);
    // Deferred a frame: the panel doesn't exist in the DOM until this render commits.
    window.requestAnimationFrame(() => panelRef.current?.focus());
  };

  const close = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="tw-mb-5">
      <div className="tw-flex tw-items-baseline tw-gap-3">
        {label && (
          <Text className="tw-text-very-dark-grey" size="sm" tag="span" weight="bold">{label}</Text>
        )}
        <UnstyledButton
          ref={triggerRef}
          aria-controls={panelId}
          aria-expanded={isOpen}
          className="tw-text-tci-purple tw-underline"
          onClick={isOpen ? close : open}
        >
          {isOpen ? I18n.t('hide_tags') : I18n.t('show_tags', { count: initialCount })}
        </UnstyledButton>
      </div>

      {isOpen && (
        <div ref={panelRef} className="tw-mt-2 tw-outline-none" id={panelId} tabIndex={-1}>
          <TagManagerTable
            categories={categories}
            correlations={correlations}
            error={isError}
            isLoading={isLoading}
            modelId={modelId}
            modelType={modelType}
            programId={programId}
            variant={variant}
          />
        </div>
      )}
    </div>
  );
};

export default TagManager;
